import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Pencil } from 'lucide-react'

import { AmountInput } from '@/components/AmountInput'
import { DateTimePicker } from '@/components/DateTimePicker'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { useLocalizedNumberInput } from '@/hooks/useLocalizedNumberInput'
import { cn } from '@/lib/utils'
import {
  inlineEditType,
  isInlineMoney,
  type InlineCellEdit,
  type InlineEditSpec,
  type InlineEditValue,
} from '@/lib/inlineEdit'
import { formatDate, formatNumber } from '@/utils/format'
import { formatMoney } from '@/utils/money'

/**
 * Where the cell goes once a value is committed: on to another cell, or — for
 * an editor that is still being worked in, such as a date and then its time —
 * nowhere, staying open for the next change.
 */
export type InlineEditMove = 'next' | 'prev' | 'down' | 'up' | 'stay'

const DASH = '—'

// ─── Staged value ──────────────────────────────────────────────────────────

/**
 * What a cell shows once it has been edited. The row behind it still holds the
 * old value, so the staged value is rendered from the edit itself rather than
 * by the column — which also keeps every list showing pending changes the same
 * way.
 */
function StagedValue<TData>({
  spec,
  row,
  value,
}: {
  spec: InlineEditSpec<TData>
  row: TData
  value: InlineEditValue
}) {
  const { t } = useTranslation()
  const type = inlineEditType(spec, row)

  const text = (): string => {
    if (value === null || value === '') return DASH
    if (type === 'money' && isInlineMoney(value)) {
      return formatMoney(value.amount, value.currency, spec.fractionDigits ?? 2)
    }
    if (type === 'number') return formatNumber(Number(value))
    if (type === 'bool') return value ? t('common.yes') : t('common.no')
    if (type === 'date') return formatDate(new Date(String(value)))
    if (type === 'select') {
      const option = spec.options?.(row).find((o) => o.value === value)
      return option?.label ?? String(value)
    }
    return String(value)
  }

  // The cell itself is tinted, so the value keeps the cell's own type and
  // padding: a badge here would resize the column and shift the table.
  return <span className="text-(--ad-warning)">{text()}</span>
}

// ─── Editors ───────────────────────────────────────────────────────────────

interface EditorProps<TData> {
  spec: InlineEditSpec<TData>
  row: TData
  value: InlineEditValue
  commit: (value: InlineEditValue, move?: InlineEditMove) => void
  cancel: () => void
}

/** Turns the keys every editor shares into a commit, a move or a cancel. */
function useEditorKeys<TData>({ commit, cancel }: Pick<EditorProps<TData>, 'commit' | 'cancel'>) {
  return (value: InlineEditValue) => (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commit(value, 'down')
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancel()
    } else if (e.key === 'Tab') {
      e.preventDefault()
      commit(value, e.shiftKey ? 'prev' : 'next')
    }
  }
}

function TextEditor<TData>({ spec, value, commit, cancel }: EditorProps<TData>) {
  const [draft, setDraft] = useState(value === null ? '' : String(value))
  const keys = useEditorKeys<TData>({ commit, cancel })
  return (
    <Input
      autoFocus
      value={draft}
      maxLength={spec.maxLength}
      placeholder={spec.placeholder}
      className="h-8 px-2 py-0 text-sm md:text-sm"
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={keys(draft.trim() === '' ? null : draft)}
      onBlur={() => commit(draft.trim() === '' ? null : draft)}
    />
  )
}

function NumberEditor<TData>({ spec, value, commit, cancel }: EditorProps<TData>) {
  const [draft, setDraft] = useState(value === null ? '' : String(value))
  const keys = useEditorKeys<TData>({ commit, cancel })
  const parsed = draft === '' || Number.isNaN(Number(draft)) ? null : Number(draft)
  const numberProps = useLocalizedNumberInput({
    value: draft,
    onChange: setDraft,
    min: spec.min,
    max: spec.max,
    step: spec.step ?? 'any',
    onKeyDown: keys(parsed),
  })
  return (
    <Input
      autoFocus
      {...numberProps}
      placeholder={spec.placeholder}
      className="h-8 px-2 py-0 text-sm md:text-sm"
      onBlur={() => commit(parsed)}
    />
  )
}

function MoneyEditor<TData>({ spec, row, value, commit, cancel }: EditorProps<TData>) {
  const currency = (isInlineMoney(value) ? value.currency : '') || spec.currency?.(row) || ''
  const [draft, setDraft] = useState(isInlineMoney(value) ? String(value.amount) : '')
  const keys = useEditorKeys<TData>({ commit, cancel })
  const parsed = draft.trim() === '' ? null : { amount: Number(draft), currency }
  return (
    <div onKeyDown={keys(parsed)} onBlur={() => commit(parsed)}>
      <AmountInput
        autoFocus
        value={draft}
        onChange={setDraft}
        unit={currency}
        min={spec.min ?? 0}
        max={spec.max}
        step={spec.step ?? 'any'}
        className="h-8 [&_input]:h-8 [&_input]:py-0 [&_input]:text-sm"
      />
    </div>
  )
}

function SelectEditor<TData>({ spec, row, value, commit, cancel }: EditorProps<TData>) {
  const options = spec.options?.(row) ?? []
  const current = options.find((o) => o.value === value)
  return (
    <Select
      label={spec.placeholder ?? ''}
      defaultOpen
      value={value === null ? undefined : String(value)}
      onValueChange={(next) => commit(next)}
      onOpenChange={(open) => {
        if (!open) cancel()
      }}
    >
      <SelectTrigger size="sm" className="h-8 px-2 text-sm">
        {/* Radix cannot resolve a label on its own, so it is passed in. */}
        <SelectValue>{current?.label ?? DASH}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/**
 * The date picker is a three-part control — day, time and a clear button — and
 * far wider than the column it sits in, so it floats over the row instead of
 * stretching it. It closes on Escape, or on a click outside it that is not in
 * the calendar popover it opens itself.
 */
function DateEditor<TData>({ spec, value, commit, cancel }: EditorProps<TData>) {
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (box.current?.contains(target)) return
      if (target.closest('[data-radix-popper-content-wrapper]')) return
      cancel()
    }
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') cancel()
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [cancel])

  return (
    <div
      ref={box}
      className="absolute z-20 w-80 rounded-md border border-(--ad-border) bg-(--ad-surface) p-2 shadow-md"
    >
      {/* The day and the time are picked one after the other, so each change
          is staged with the editor left open. */}
      <DateTimePicker
        value={value === null ? '' : String(value)}
        placeholder={spec.placeholder}
        onChange={(next) => commit(next || null, 'stay')}
      />
    </div>
  )
}

// ─── Cell ──────────────────────────────────────────────────────────────────

interface InlineEditCellProps<TData> {
  row: TData
  columnId: string
  spec: InlineEditSpec<TData>
  /** The cell as the column renders it, shown until the field is edited. */
  children: ReactNode
  /** Set once the cell has been edited but not yet saved. */
  staged?: InlineCellEdit
  /** True while this is the cell the editor is open on. */
  active: boolean
  onActivate: () => void
  onClose: () => void
  onCommit: (value: InlineEditValue, move?: InlineEditMove) => void
}

/**
 * A table cell that can be edited in place. It reads as an ordinary cell until
 * the list is put in edit mode, then offers an editor on click and shows what
 * the field will become once the staged changes are saved.
 */
export function InlineEditCell<TData>({
  row,
  spec,
  children,
  staged,
  active,
  onActivate,
  onClose,
  onCommit,
}: InlineEditCellProps<TData>) {
  const { t } = useTranslation()
  // Escape closes the editor, and the blur that follows must not then save the
  // value the user just abandoned.
  const cancelled = useRef(false)
  // An editor that has finished commits once. Without this, the blur that
  // follows a commit — moving on with Tab unmounts the editor under the
  // cursor — would stage the same value a second time and undo the move it was
  // part of. An editor that stays open goes on committing.
  const settled = useRef(false)

  useEffect(() => {
    if (!active) {
      cancelled.current = false
      settled.current = false
    }
  }, [active])
  const readOnly = spec.readOnly?.(row) ?? false
  const type = inlineEditType(spec, row)
  const value = staged ? staged.value : spec.value(row)

  const commit = (next: InlineEditValue, move?: InlineEditMove) => {
    if (cancelled.current || settled.current) return
    const error = spec.validate?.(next, row)
    if (error) {
      toast.error(error, { duration: 4000 })
      return
    }
    if (move !== 'stay') settled.current = true
    onCommit(next, move)
  }

  const cancel = () => {
    cancelled.current = true
    onClose()
    // The flag only has to outlive the blur the close causes.
    setTimeout(() => {
      cancelled.current = false
    }, 0)
  }

  if (readOnly) {
    return <>{staged ? <StagedValue spec={spec} row={row} value={value} /> : children}</>
  }

  // A switch is the whole editor — it takes one click, so the cell has no
  // separate open state to enter.
  if (type === 'bool') {
    return (
      <span className="flex items-center gap-2">
        <Switch
          checked={value === true}
          // A switch has no open state to close, so every toggle of it is a
          // commit in its own right.
          onCheckedChange={(checked) => {
            settled.current = false
            commit(checked)
          }}
          aria-label={t('common.edit')}
        />
        {staged && <StagedValue spec={spec} row={row} value={value} />}
      </span>
    )
  }

  if (active) {
    const editorProps = { spec, row, value, commit, cancel }
    // The editor is taller than a line of text and pads its value in from a
    // border, so the negative margins let it spill into the cell's padding:
    // the row keeps its height and the value stays where it was.
    return (
      <div className={cn('relative -mx-2 -my-1.5 min-w-0', spec.editorClassName ?? 'max-w-56')}>
        {type === 'money' ? (
          <MoneyEditor {...editorProps} />
        ) : type === 'number' ? (
          <NumberEditor {...editorProps} />
        ) : type === 'select' ? (
          <SelectEditor {...editorProps} />
        ) : type === 'date' ? (
          <DateEditor {...editorProps} />
        ) : (
          <TextEditor {...editorProps} />
        )}
      </div>
    )
  }

  return (
    <div
      role="button"
      tabIndex={0}
      title={t('inlineEdit.editField')}
      onClick={onActivate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onActivate()
        }
      }}
      // The padding is cancelled by the margin, and the pencil is taken out of
      // the flow: an editable cell has to occupy exactly the room the plain
      // cell did, or the whole table resizes when edit mode is switched on.
      className="group/cell relative -m-1 cursor-text rounded-sm p-1 outline-none hover:bg-(--ad-primary-soft) focus-visible:ring-2 focus-visible:ring-(--ad-primary)"
    >
      {staged ? <StagedValue spec={spec} row={row} value={value} /> : children}
      <Pencil className="pointer-events-none absolute top-1 right-0.5 size-3 text-(--ad-primary) opacity-0 transition-opacity group-hover/cell:opacity-100" />
    </div>
  )
}
